import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const REPORT_CARDS = [
  { id: 'trial-balance', title: 'Trial Balance',                       description: 'View all account debit and credit balances to verify that total debits equal total credits.' },
  { id: 'balance-sheet', title: 'Balance Sheet',                       description: 'View assets, liabilities, and equity at a point in time.' },
  { id: 'ar-ageing',     title: 'AR Ageing Report',                    description: 'View outstanding customer receivables grouped by age.' },
  { id: 'ap-ageing',     title: 'AP Ageing Report',                    description: 'View outstanding supplier payables grouped by age.' },
];

/* ─── Loading / Error helpers ─────────────────────────── */
const Loader = () => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</div>
);
const Err = ({ msg }) => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {msg}</div>
);
const Empty = ({ label }) => (
  <tr><td colSpan={10}>
    <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
      <div style={{ fontSize: '2rem' }}>📊</div>
      <div style={{ fontWeight: 600, marginTop: '0.5rem' }}>No {label} data available</div>
    </div>
  </td></tr>
);

/* ─── Trial Balance ───────────────────────────────────── */
const TrialBalanceView = () => {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    accountingApi.getTrialBalance()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  const totalDebit  = rows.reduce((s, r) => s + (parseFloat(r.debit)  || 0), 0);
  const totalCredit = rows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Account</th>
            <th className="text-right">Debit (USD)</th>
            <th className="text-right">Credit (USD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="trial balance" /> : rows.map((r, i) => (
            <tr key={i}>
              <td>{r.account || r.account_name || r.name || '—'}</td>
              <td className="text-right" style={{ color: parseFloat(r.debit) ? '#10b981' : '#94a3b8' }}>
                {parseFloat(r.debit) ? fmt(r.debit) : '—'}
              </td>
              <td className="text-right" style={{ color: parseFloat(r.credit) ? '#ef4444' : '#94a3b8' }}>
                {parseFloat(r.credit) ? fmt(r.credit) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--etpos-bg,#f8fafc)' }}>
              <td>Total</td>
              <td className="text-right" style={{ color: '#10b981' }}>{fmt(totalDebit)}</td>
              <td className="text-right" style={{ color: '#ef4444' }}>{fmt(totalCredit)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

/* ─── Balance Sheet ───────────────────────────────────── */
const BalanceSheetView = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getBalanceSheet()
      .then(res => setData(res.data || res))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;
  if (!data)   return <Err msg="No data returned" />;

  const sections = [
    { key: 'assets',      title: 'Assets',      items: data.assets      || [] },
    { key: 'liabilities', title: 'Liabilities', items: data.liabilities || [] },
    { key: 'equity',      title: 'Equity',       items: data.equity      || [] },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: '1rem' }}>
      {sections.map(sec => (
        <div key={sec.key} className="acc-card">
          <h3 className="acc-card-title">{sec.title}</h3>
          <table className="acc-table">
            <tbody>
              {sec.items.length === 0 ? (
                <tr><td colSpan={2} style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No data</td></tr>
              ) : sec.items.map((r, i) => (
                <tr key={i} style={r.total || r.is_total ? { fontWeight: 700, background: 'var(--etpos-bg,#f8fafc)' } : {}}>
                  <td style={{ paddingLeft: r.total || r.is_total ? '1rem' : '1.75rem' }}>
                    {r.label || r.account || r.name || '—'}
                  </td>
                  <td className="text-right">{fmt(r.amount || r.balance || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

/* ─── AR Ageing ───────────────────────────────────────── */
const ArAgeingView = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getArAgeing()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th className="text-right">Current</th>
            <th className="text-right">1–30 Days</th>
            <th className="text-right">31–60 Days</th>
            <th className="text-right">61–90 Days</th>
            <th className="text-right">90+ Days</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="AR ageing" /> : rows.map((r, i) => {
            const current = parseFloat(r.current || r.current_amount || 0);
            const d30     = parseFloat(r.days_30 || r['1_30'] || r.days30 || 0);
            const d60     = parseFloat(r.days_60 || r['31_60'] || r.days60 || 0);
            const d90     = parseFloat(r.days_90 || r['61_90'] || r.days90 || 0);
            const over90  = parseFloat(r.over_90 || r['90_plus'] || r.over90 || 0);
            const total   = current + d30 + d60 + d90 + over90;
            return (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.customer || r.contact_name || r.name || '—'}</td>
                <td className="text-right">{fmt(current)}</td>
                <td className="text-right">{fmt(d30)}</td>
                <td className="text-right">{fmt(d60)}</td>
                <td className="text-right">{fmt(d90)}</td>
                <td className="text-right" style={{ color: over90 ? '#ef4444' : '#94a3b8' }}>{fmt(over90)}</td>
                <td className="text-right" style={{ fontWeight: 700 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─── AP Ageing ───────────────────────────────────────── */
const ApAgeingView = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getApAgeing()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th className="text-right">Current</th>
            <th className="text-right">1–30 Days</th>
            <th className="text-right">31–60 Days</th>
            <th className="text-right">61–90 Days</th>
            <th className="text-right">90+ Days</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="AP ageing" /> : rows.map((r, i) => {
            const current = parseFloat(r.current || r.current_amount || 0);
            const d30     = parseFloat(r.days_30 || r['1_30'] || r.days30 || 0);
            const d60     = parseFloat(r.days_60 || r['31_60'] || r.days60 || 0);
            const d90     = parseFloat(r.days_90 || r['61_90'] || r.days90 || 0);
            const over90  = parseFloat(r.over_90 || r['90_plus'] || r.over90 || 0);
            const total   = current + d30 + d60 + d90 + over90;
            return (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.supplier || r.contact_name || r.name || '—'}</td>
                <td className="text-right">{fmt(current)}</td>
                <td className="text-right">{fmt(d30)}</td>
                <td className="text-right">{fmt(d60)}</td>
                <td className="text-right">{fmt(d90)}</td>
                <td className="text-right" style={{ color: over90 ? '#ef4444' : '#94a3b8' }}>{fmt(over90)}</td>
                <td className="text-right" style={{ fontWeight: 700 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─── View map ────────────────────────────────────────── */
const VIEWS = {
  'trial-balance': { component: <TrialBalanceView />, title: 'Trial Balance'       },
  'balance-sheet': { component: <BalanceSheetView />, title: 'Balance Sheet'       },
  'ar-ageing':     { component: <ArAgeingView />,     title: 'AR Ageing Report'    },
  'ap-ageing':     { component: <ApAgeingView />,     title: 'AP Ageing Report'    },
};

/* ─── Report Card ─────────────────────────────────────── */
const ReportCard = ({ card, onView }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' }} />
    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{card.title}</h3>
    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, flexGrow: 1 }}>{card.description}</p>
    <div>
      <button
        onClick={() => onView(card.id)}
        style={{ background: '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
        onMouseEnter={e => e.target.style.background = '#4c1d95'}
        onMouseLeave={e => e.target.style.background = '#5b21b6'}
      >
        View Report
      </button>
    </div>
  </div>
);

/* ─── Main Component ──────────────────────────────────── */
const AccountingReports = () => {
  const [activeView, setActiveView] = useState(null);

  if (activeView) {
    const view = VIEWS[activeView];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => setActiveView(null)}
              className="acc-btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.875rem' }}
            >
              ← Back to Reports
            </button>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>{view.title}</h2>
          </div>
          <button
            onClick={() => window.print()}
            className="acc-btn-secondary"
          >
            🖨 Print
          </button>
        </div>
        <div className="table-content-card" style={{ padding: '1.25rem' }}>
          {view.component}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: '1.25rem' }}>
        {REPORT_CARDS.map(card => (
          <ReportCard key={card.id} card={card} onView={setActiveView} />
        ))}
      </div>
    </div>
  );
};

export default AccountingReports;
